import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';

import { muiPalette } from '@/shared/general-theme';

const frameworks = [
  {
    title: 'BSIMM',
    fullName: 'Building Security In Maturity Model',
    description: 'A descriptive model based on real-world practices from leading software organizations.',
    accent: muiPalette.primary.main,
  },
  {
    title: 'NIST SSDF',
    fullName: 'Secure Software Development Framework',
    description: 'Guidelines derived from industry best practices for secure software lifecycle management.',
    accent: muiPalette.secondary.main,
  },
  {
    title: 'OWASP SAMM',
    fullName: 'Software Assurance Maturity Model',
    description: 'A prescriptive model designed to help organizations formulate a product security strategy.',
    accent: muiPalette.tertiary.main,
  },
  {
    title: 'ISA/IEC 62443',
    fullName: 'Security for Industrial Automation',
    description: 'The international standard for the secure development lifecycle of industrial products.',
    accent: muiPalette.success.main,
  },
];

const MaturitySection: React.FC = () => {
  return (
    <Box component='section'>
      <Stack spacing={2} sx={{ mb: 8, textAlign: 'center', alignItems: 'center' }}>
        <Typography variant='h3' color='white' sx={{ fontWeight: 700 }}>
          The Strategic Path to Maturity
        </Typography>
        <Typography variant='h6' color={muiPalette.grey[300]} sx={{ maxWidth: '800px', mx: 'auto' }}>
          Measuring maturity is the first step toward strategic improvement. We integrate globally recognized
          frameworks to give you factual data for your security roadmap.
        </Typography>
      </Stack>

      <Grid container spacing={4}>
        {frameworks.map((framework, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <motion.div
              whileHover={{ y: -8 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              style={{ height: '100%' }}
            >
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: muiPalette.background.dark,
                  borderColor: muiPalette.border.dark,

                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '4px',
                    backgroundColor: framework.accent,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <VerifiedUserIcon sx={{ color: framework.accent, mb: 2, fontSize: '2rem' }} />
                  <Typography variant='h6' sx={{ color: 'white', mb: 1, fontWeight: 700 }}>
                    {framework.title}
                  </Typography>
                  <Typography variant='body2' sx={{ color: muiPalette.grey[200], fontSize: '0.85rem' }}>
                    {framework.fullName}
                  </Typography>
                  <Typography variant='body2' sx={{ mt: 2, color: muiPalette.grey[400], lineHeight: 1.5 }}>
                    {framework.description}
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default MaturitySection;
